import React, { useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Import layout components
import { AdminLayout } from '../components/layout/AdminLayout';
import { AuthenticatedLayout } from '../components/layout/AuthenticatedLayoutNew';
import { AuthenticatedLayoutV2 } from '../components/layout/AuthenticatedLayoutV2';
import { AuthenticatedLayoutStudent } from '../components/layout/AuthenticatedLayoutStudent';
import { Layout } from '../components/layout/Layout';

// Import route guard
import { RouteGuard } from './RouteGuard';

// Import types
import type { RouteConfig } from './types';

interface RouteRendererProps {
  routes: RouteConfig[];
}

/**
 * SEO wrapper component that applies page metadata
 */
interface SEOWrapperProps {
  config: RouteConfig;
  children: React.ReactNode;
}

function SEOWrapper({ config, children }: SEOWrapperProps) {
  const { seo } = config;

  if (!seo) {
    return children;
  }

  return (
    <>
      <Helmet>
        <title>{seo.title} - RollOps</title>
        {seo.noindex && (
          <>
            <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
            <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
          </>
        )}
      </Helmet>
      {children}
    </>
  );
}

/**
 * Route groups organized by layout type
 */
interface RouteGroups {
  authenticated: RouteConfig[];
  'authenticated-v2': RouteConfig[];
  'authenticated-student': RouteConfig[];
  admin: RouteConfig[];
  public: RouteConfig[];
  none: RouteConfig[];
}

/**
 * Group routes by their layout type for nested routing
 */
function groupRoutesByLayout(routes: RouteConfig[]): RouteGroups {
  const groups: RouteGroups = {
    authenticated: [],
    'authenticated-v2': [],
    'authenticated-student': [],
    admin: [],
    public: [],
    none: [],
  };

  routes.forEach((route) => {
    const layoutType = route.layout || 'none';
    if (layoutType in groups) {
      groups[layoutType as keyof RouteGroups].push(route);
    } else {
      // Fallback for any unknown layout types
      groups.none.push(route);
    }
  });

  return groups;
}

/**
 * Render a single route element with SEO and guards (but no layout - layout is parent)
 */
function RouteElement({ config }: { config: RouteConfig }) {
  return (
    <SEOWrapper config={config}>
      <RouteGuard config={config}>
        <config.component />
      </RouteGuard>
    </SEOWrapper>
  );
}

/**
 * Main route renderer that converts route configurations into React Router Routes
 * 
 * Uses nested routing to keep layouts persistent across page navigation.
 * This prevents layout remounting and eliminates flickering when navigating
 * between pages that share the same layout.
 * 
 * Structure:
 * - Authenticated routes are nested under AuthenticatedLayout
 * - Admin routes are nested under AdminLayout
 * - Public routes are nested under Layout
 * - Routes with no layout are rendered directly
 */
export function RouteRenderer({ routes }: RouteRendererProps) {
  // Group routes by layout type - memoized to avoid recalculation
  const routeGroups = useMemo(() => groupRoutesByLayout(routes), [routes]);

  return (
    <Routes>
      {/* Authenticated routes - layout stays mounted during navigation */}
      {routeGroups.authenticated.length > 0 && (
        <Route element={<AuthenticatedLayout />}>
          {routeGroups.authenticated.map((config) => (
            <Route
              key={config.path}
              path={config.path}
              element={<RouteElement config={config} />}
            />
          ))}
        </Route>
      )}

      {/* Authenticated V2 routes - new layout shell (TopBarV2 + SidebarV2) */}
      {routeGroups['authenticated-v2'].length > 0 && (
        <Route element={<AuthenticatedLayoutV2 />}>
          {routeGroups['authenticated-v2'].map((config) => (
            <Route
              key={config.path}
              path={config.path}
              element={<RouteElement config={config} />}
            />
          ))}
        </Route>
      )}

      {/* Student routes - student layout shell (TopBarV2 + StudentSidebar) */}
      {routeGroups['authenticated-student'].length > 0 && (
        <Route element={<AuthenticatedLayoutStudent />}>
          {routeGroups['authenticated-student'].map((config) => (
            <Route
              key={config.path}
              path={config.path}
              element={<RouteElement config={config} />}
            />
          ))}
        </Route>
      )}

      {/* Admin routes - layout stays mounted during navigation */}
      {routeGroups.admin.length > 0 && (
        <Route element={<AdminLayout />}>
          {routeGroups.admin.map((config) => (
            <Route
              key={config.path}
              path={config.path}
              element={<RouteElement config={config} />}
            />
          ))}
        </Route>
      )}

      {/* Public routes - layout stays mounted during navigation */}
      {routeGroups.public.length > 0 && (
        <Route element={<Layout />}>
          {routeGroups.public.map((config) => (
            <Route
              key={config.path}
              path={config.path}
              element={<RouteElement config={config} />}
            />
          ))}
        </Route>
      )}

      {/* Routes with no layout - rendered directly */}
      {routeGroups.none.map((config) => (
        <Route
          key={config.path}
          path={config.path}
          element={<RouteElement config={config} />}
        />
      ))}
    </Routes>
  );
}